// Shared calendar helpers — facility colors + overlap detection.
// Used by Calendar.jsx (live view) and PrintableDayPlanner.jsx (print view)
// so both stay visually consistent.

// Palette for facilities that don't have an explicit color set.
// Green (#10B981) is reserved for Personal, red for conflicts — avoided here.
export const FACILITY_PALETTE = [
  "#3B82F6", // blue
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6366F1", // indigo
  "#EAB308", // yellow
];

export const PERSONAL_COLOR = "#10B981";
export const DEFAULT_WORK_COLOR = "#6B7280";
export const CONFLICT_COLOR = "#EF4444";

/**
 * Build a stable {facility_id -> color} map.
 * Uses fac.color if the user set one, otherwise assigns the next distinct
 * color from FACILITY_PALETTE in sorted facility order.
 */
export function buildFacilityColorMap(facilities = []) {
  const sorted = [...facilities].sort((a, b) =>
    (a.facility_id || "").localeCompare(b.facility_id || "")
  );
  const map = {};
  let paletteIdx = 0;
  for (const fac of sorted) {
    if (fac.color) {
      map[fac.facility_id] = fac.color;
    } else {
      map[fac.facility_id] = FACILITY_PALETTE[paletteIdx % FACILITY_PALETTE.length];
      paletteIdx += 1;
    }
  }
  return map;
}

/**
 * Return the effective color for a meeting:
 *   - personal → PERSONAL_COLOR
 *   - linked facility → colorMap[facility_id]
 *   - otherwise → meeting.facility_color (if backend-enriched) or default work gray
 */
export const STUDY_COLOR = "#A78BFA";

export function getMeetingColor(meeting, colorMap = {}) {
  if (meeting?.source === "certprep") return STUDY_COLOR;
  if (meeting?.category === "personal") return PERSONAL_COLOR;
  if (meeting?.facility_id && colorMap[meeting.facility_id]) {
    return colorMap[meeting.facility_id];
  }
  return meeting?.facility_color || DEFAULT_WORK_COLOR;
}

/**
 * Given a list of meetings that all occur on the same day, return the Set
 * of meeting_ids that conflict (overlap in time) with at least one other.
 * Overlap rule: a.start < b.end && b.start < a.end (exclusive end).
 */
export function findConflictingMeetingIds(dayMeetings = []) {
  const conflicts = new Set();
  const list = [...dayMeetings].sort((a, b) => a.start_time - b.start_time);
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i];
      const b = list[j];
      if (a.start_time < b.end_time && b.start_time < a.end_time) {
        if (a.meeting_id) conflicts.add(a.meeting_id);
        if (b.meeting_id) conflicts.add(b.meeting_id);
      }
    }
  }
  return conflicts;
}

/**
 * Group meetings into same-day buckets keyed by YYYY-MM-DD, then return a
 * combined Set of all meeting_ids involved in any conflict across all days
 * covered in the input. Useful for week/month views.
 */
export function findAllConflictingIds(meetings = []) {
  const byDate = {};
  for (const m of meetings) {
    const k = m.date;
    if (!k) continue;
    if (!byDate[k]) byDate[k] = [];
    byDate[k].push(m);
  }
  const all = new Set();
  for (const day of Object.values(byDate)) {
    const ids = findConflictingMeetingIds(day);
    ids.forEach((id) => all.add(id));
  }
  return all;
}

/**
 * Group meetings into clusters (transitively overlapping), then assign each
 * meeting to the lowest-index "lane" where no earlier meeting in its cluster
 * still occupies that lane. Returns [{ meeting, lane, totalLanes }].
 */
export function layoutDayMeetings(meetings = []) {
  const sorted = [...meetings].sort(
    (a, b) => a.start_time - b.start_time || a.end_time - b.end_time
  );
  const result = [];
  let cluster = [];
  let clusterEnd = 0;

  const flushCluster = () => {
    if (cluster.length === 0) return;
    const laneEnds = []; // laneEnds[i] = end_time of last meeting placed in lane i
    const assigned = [];
    for (const m of cluster) {
      let placed = false;
      for (let i = 0; i < laneEnds.length; i += 1) {
        if (laneEnds[i] <= m.start_time) {
          assigned.push({ meeting: m, lane: i });
          laneEnds[i] = m.end_time;
          placed = true;
          break;
        }
      }
      if (!placed) {
        assigned.push({ meeting: m, lane: laneEnds.length });
        laneEnds.push(m.end_time);
      }
    }
    const totalLanes = laneEnds.length;
    for (const entry of assigned) {
      result.push({ ...entry, totalLanes });
    }
    cluster = [];
    clusterEnd = 0;
  };

  for (const m of sorted) {
    if (cluster.length === 0) {
      cluster.push(m);
      clusterEnd = m.end_time;
    } else if (m.start_time < clusterEnd) {
      cluster.push(m);
      if (m.end_time > clusterEnd) clusterEnd = m.end_time;
    } else {
      flushCluster();
      cluster.push(m);
      clusterEnd = m.end_time;
    }
  }
  flushCluster();
  return result;
}
